export type LanguageItem = {
	code: string;
	label: string;
	flagIso: string;
	isDisabled?: boolean;
};

export type LanguagesContent = {
	languages: LanguageItem[];
	defaultLanguageCode: string;
};

export const createEmptyLanguage = (): LanguageItem => ({
	code: "",
	label: "",
	flagIso: "",
	isDisabled: false
});

export const normalizeSegment = (value?: string | null) => (value ?? "").trim();

export const normalizeLanguageComparable = (code?: string | null, label?: string | null, flagIso?: string | null) => ({
	code: normalizeSegment(code).toLowerCase(),
	label: normalizeSegment(label),
	flagIso: normalizeSegment(flagIso).toLowerCase()
});

export const buildComparableFromContent = (content?: LanguagesContent | null) => {
	if (!content?.languages || content.languages.length === 0) return null;
	const active = content.languages
		.filter((item) => !item.isDisabled)
		.map((item) => normalizeLanguageComparable(item.code, item.label, item.flagIso))
		.filter((item) => item.code.length > 0)
		.sort((a, b) => a.code.localeCompare(b.code));
	if (active.length === 0) return null;
	return {
		defaultLanguageCode: normalizeSegment(content.defaultLanguageCode).toLowerCase(),
		languages: active
	};
};

export const areComparablesEqual = (
	left: ReturnType<typeof buildComparableFromContent>,
	right: ReturnType<typeof buildComparableFromContent>
) => {
	if (!left || !right) return false;
	if (left.defaultLanguageCode !== right.defaultLanguageCode) return false;
	if (left.languages.length !== right.languages.length) return false;
	return left.languages.every((language, index) => {
		const other = right.languages[index];
		return language.code === other.code && language.label === other.label && language.flagIso === other.flagIso;
	});
};

export const sanitizeContent = (content?: LanguagesContent | null): LanguagesContent => {
	const languages = (content?.languages ?? []).map((item) => ({
		code: normalizeSegment(item.code),
		label: normalizeSegment(item.label),
		flagIso: normalizeSegment(item.flagIso),
		isDisabled: item.isDisabled ?? false
	}));
	const defaultCode =
		normalizeSegment(content?.defaultLanguageCode) || (languages.find((l) => !l.isDisabled)?.code ?? "");
	return {
		languages: languages.length > 0 ? languages : [createEmptyLanguage()],
		defaultLanguageCode: defaultCode
	};
};
